// src/components/Footer.tsx
//
// UPDATED — CTA unified with header/hero: "Get a program map" is the
// single primary action, matching Header.tsx and LandingView.tsx.
// MCP-copy link kept as a secondary footer-only path.
//
// FIXED — footer grid had no outer max-w-screen-xl container, so its
// content sat at a different effective width than every section above
// it (all of which wrap content in max-w-screen-xl mx-auto). Added the
// same wrapper here so the footer aligns with the rest of the page.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import { Button } from './ui/button';

const FDW_UID = '3vuuC2kHE5ciUd1r3IKNVs7SEfp1';
const FDW_MCP_URL = `https://fivedollarworkday.com/mcp/e/${FDW_UID}`;

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const handleCopyAndLaunch = async () => {
    try {
      await navigator.clipboard.writeText(FDW_MCP_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    } catch (err) {
      console.error('Failed to copy to clipboard', err);
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (window.location.pathname === '/') {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  return (
    <footer className="px-6 md:px-12 py-32 bg-brand-white border-t border-brand-border">
      <div className="max-w-screen-xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16">

        {/* ── Left — positioning + primary CTA ────────────────────── */}
        <div>
          <h2 className="text-3xl font-medium tracking-tight text-brand-black mb-6">
            Five Dollar Workday
          </h2>
          <p className="text-brand-charcoal text-lg leading-relaxed max-w-md font-light mb-8">
            FDW runs reconciliation, funding, and exception review across Marqeta and Lithic —
            so your team only ever sees what actually needs a decision.
          </p>
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate('/signup?next=/chat&intent=program-map')}
              className="w-fit"
            >
              Get a program map
            </Button>
            <p className="text-xs text-brand-gray font-light">
              Create an account. Tell the agent your processor. It books the 30 minutes.
            </p>
          </div>
        </div>

        {/* ── Right — navigation ───────────────────────────────────── */}
        <div className="flex flex-col justify-between">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 text-sm font-light">

            <div>
              <h3 className="text-brand-gray mb-6 uppercase tracking-[0.2em] text-xs font-mono">
                Platform
              </h3>
              <ul className="space-y-4">
                <li>
                  <button onClick={() => scrollToSection('how-it-works')} className="text-brand-charcoal hover:text-brand-black transition-colors text-left">
                    How it works
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/security')} className="text-brand-charcoal hover:text-brand-black transition-colors text-left">
                    Security
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/dashboard')} className="text-brand-charcoal hover:text-brand-black transition-colors text-left">
                    Go to dashboard
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-brand-gray mb-6 uppercase tracking-[0.2em] text-xs font-mono">
                Product
              </h3>
              <ul className="space-y-4">
                <li>
                  <a href="https://fivedollarworkday.com/registry" className="text-brand-charcoal hover:text-brand-black transition-colors">
                    Registry
                  </a>
                </li>
                <li className="pt-2 border-t border-brand-border/50">
                  {!copied ? (
                    <button onClick={handleCopyAndLaunch} className="group flex items-center gap-1.5 text-brand-gray hover:text-brand-charcoal transition-colors text-xs font-mono" title="Copy our server URL to paste into your AI">
                      <Copy className="w-3 h-3 group-hover:text-brand-black transition-colors" />
                      Or connect via MCP
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5 text-green-600 text-xs font-mono font-medium">
                      <Check className="w-3 h-3" /> Copied!
                    </span>
                  )}
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-brand-gray mb-6 uppercase tracking-[0.2em] text-xs font-mono">
                Company
              </h3>
              <ul className="space-y-4">
                <li>
                  <button onClick={() => navigate('/blog')} className="text-brand-charcoal hover:text-brand-black transition-colors text-left">
                    Build log
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/about')} className="text-brand-charcoal hover:text-brand-black transition-colors text-left">
                    About
                  </button>
                </li>
                <li>
                  <a href="https://x.com/FiveWorkday" target="_blank" rel="noopener noreferrer" className="text-brand-charcoal hover:text-brand-black transition-colors">
                    @FiveWorkday
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-20 space-y-6 border-t border-brand-border pt-8">
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs text-brand-gray tracking-wide">
              <button onClick={() => navigate('/privacy')} className="hover:text-brand-black transition-colors">Privacy policy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-brand-black transition-colors">Terms of service</button>
            </div>
            <div className="text-xs text-brand-gray font-mono">
              © {new Date().getFullYear()} Five Dollar Workday. An AI Native Service Business.
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;